import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as OV from 'online-3d-viewer';

interface Viewer3DProps {
  modelUrl: string;
  fileName?: string;
  moldNumber?: string;    // 금형번호
  moldName?: string;      // 금형명
  productName?: string;   // 제품명
  width?: string | number | { xs?: number; sm?: number; md?: number };
  height?: string | number | { xs?: number; sm?: number; md?: number };
}

export const Viewer3D: React.FC<Viewer3DProps> = ({ 
  modelUrl,
  fileName = 'model.obj',
  moldNumber,
  moldName,
  productName,
  width = '100%', 
  height = 500 
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstanceRef = useRef<any>(null);
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleOpenFullscreen = () => {
    // # 뒤의 model 파라미터는 인코딩하지 않음 (Online3DViewer 요구사항)
    let fullscreenUrl = `/o3dv/viewer.html#model=${modelUrl}$${fileName}`;
    
    // 금형 정보를 쿼리 파라미터로 추가
    const params = new URLSearchParams();
    if (moldNumber) params.append('moldNumber', moldNumber);
    if (moldName) params.append('moldName', moldName);
    if (productName) params.append('productName', productName);
    
    if (params.toString()) {
      fullscreenUrl = `/o3dv/viewer.html?${params.toString()}#model=${modelUrl}$${fileName}`;
    }
    
    console.log('Opening viewer with URL:', modelUrl);
    console.log('FileName:', fileName);
    console.log('MoldNumber:', moldNumber);
    console.log('MoldName:', moldName);
    console.log('ProductName:', productName);
    console.log('Full viewer URL:', fullscreenUrl);
    window.open(fullscreenUrl, '_blank');
  };

  useEffect(() => {
    isMountedRef.current = true;

    if (!viewerRef.current) return;

    // 이미 로딩 중이면 무시
    if (isLoadingRef.current) {
      console.log('Already loading, skip...');
      return;
    }

    // 기존 viewer가 있으면 먼저 정리
    if (viewerInstanceRef.current) {
      console.log('Destroying existing viewer...');
      viewerInstanceRef.current.Destroy();
      viewerInstanceRef.current = null;
    }

    const currentViewer = viewerRef.current;

    const loadModel = async () => {
      isLoadingRef.current = true;

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching 3D file from:', modelUrl);
        
        // CORS 옵션 추가
        const response = await fetch(modelUrl, {
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Accept': '*/*',
          }
        });
        
        if (!response.ok) {
          throw new Error(`파일 다운로드 실패 (${response.status}): ${response.statusText}`);
        }

        const blob = await response.blob();
        console.log('Blob received:', blob.size, 'bytes, type:', blob.type);
        
        const file = new File([blob], fileName, { type: blob.type });

        // 언마운트되었으면 중단
        if (!isMountedRef.current) {
          console.log('Component unmounted, abort loading');
          return;
        }

        console.log('Creating viewer...');
        const viewer = new OV.EmbeddedViewer(currentViewer, {
          backgroundColor: new OV.RGBAColor(245, 245, 245, 255),
          defaultColor: new OV.RGBColor(200, 200, 200),
          edgeSettings: new OV.EdgeSettings(false, new OV.RGBColor(0, 0, 0), 1),
        });

        viewerInstanceRef.current = viewer;

        console.log('Loading model from file...');
        viewer.LoadModelFromFileList([file]);

        setTimeout(() => {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }, 1500);

      } catch (err: any) {
        console.error('3D 뷰어 로딩 오류:', err);
        
        // CORS 에러 감지
        const isCorsError = err.name === 'TypeError' && err.message.includes('Failed to fetch');
        const errorMessage = isCorsError 
          ? '3D 모델 로딩 중 CORS 오류가 발생했습니다. 파일 접근 권한을 확인해주세요.'
          : `3D 모델을 불러오는 중 오류가 발생했습니다: ${err.message}`;
        
        if (isMountedRef.current) {
          setError(errorMessage);
          setLoading(false);
        }
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadModel();

    // Cleanup
    return () => {
      console.log('Cleanup called');
      isMountedRef.current = false;
      
      if (viewerInstanceRef.current) {
        console.log('Destroying viewer in cleanup...');
        viewerInstanceRef.current.Destroy();
        viewerInstanceRef.current = null;
      }
    };
  }, [modelUrl, fileName]);


  return (
    <Box sx={{ 
      position: 'relative', 
      width, 
      height,
      minHeight: { xs: 280, sm: 320, md: 380 } // 최소 높이 보장
    }}>
      {/* 전체화면 버튼 */}
      <Tooltip title="새 창에서 전체 기능으로 보기">
        <IconButton
          onClick={handleOpenFullscreen}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': {
              bgcolor: 'background.paper',
              boxShadow: 2
            }
          }}
        >
          <OpenInNewIcon />
        </IconButton>
      </Tooltip>

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress />
            <Box sx={{ mt: 2, color: 'text.secondary' }}>
              3D 모델 로딩 중...
            </Box>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <div 
        ref={viewerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden'
        }} 
      />
    </Box>
  );
};
